import React from 'react';
import loadable from '@loadable/component';
import { monorepoRoutePaths } from '@apps/admin/route.paths';
import { NavigationRoot, SidebarRoot, ContentWrapper } from '@apps/admin/routes/Dashboard/components/DashboardContentV2/DashboardContentV2.styles';
import { useRouterHelper } from '@shared/core';
import { useMount } from '@shared/utils/hooks/useMount';
import { PrimaryDashboardContent } from './PrimaryDashboardContent';
import { ShareDialog } from '../../../../components/ShareDialog/ShareDialog';
import { ProductSelectionDialog } from './ProductSelectionDialog/ProductSelectionDialog';
import { useDashboardContentV2Controller } from './DashboardContentV2.controller';
import { DashboardRoot } from './DashboardContentV2.styles';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { DashboardData } from '../../useDashboardData';
import { BookingBanner } from './BookingBanner/BookingBanner';
import { BookingDialog } from './BookingDialog/BookingDialog';
import { PrintEarlyAccessDialog } from './PrintEarlyAccessDialog/PrintEarlyAccessDialog';
import { WonBookingBanner } from './WonBookingBanner';
import { WonBookingDialog } from './WonBookingDialog';
import { ShippingAddressReminderBanner } from './ShippingAddressReminderBanner';
import { useShippingAddressReminder } from './hooks/useShippingAddressReminder';
import { ShippingAddressReminderDialog } from '@apps/admin/common/ShippingAddress/ShippingAddress';
import ReEngagementPopup from './ReEngagementPopup/ReEngagementPopup';
import { MobileContent } from './MobileDrawer/MobileDrawer';
import { DashboardContentV3 } from '../DashboardContentV3';

const DashboardPreview = loadable(() => import('@apps/admin/routes/DashboardPreview'));

export interface DashboardContentV2Props {
  eventDisplayName: string;
  eventHandle: string;
  dashboardData: DashboardData;
  eventId: string;
}

export const DashboardContentV2: React.FC<DashboardContentV2Props> = ({ eventDisplayName, eventHandle, dashboardData, eventId }) => {
  const { dashboard } = useDashboardTelemetry();
  const {
    data,
    error,
    eventByNameData,
    eventPassword,
    handleValuePropositionClick,
    handleProductSelectionClose,
    handleProductSelectionToggleClick,
    onProductSelectionOpen,
    handleShareDialogOpen,
    handleBookingDialogOpenFromBanner,
    bookingDialogSourceIsBanner,
    handleDismissBannerClick,
    isBannerVisible,
    isMobile,
    isOpen,
    isProductSelectionOpen,
    guestSiteLink,
    shouldRefreshDashboardPreview,
    refreshDashboardPreview,
    loading,
    onClose,
    productRows,
    isBookingDialogOpen,
    onBookingDialogClose,
    isPrintEarlyAccessDialogOpen,
    onPrintEarlyAccessDialogClose,
    isWonBannerVisible,
    isWonBookingDialogOpen,
    handleDismissWonBannerClick,
    handleOnWonBookingDialogClose,
    isChecklistVisible,
    shouldHidePopups
  } = useDashboardContentV2Controller(eventHandle, dashboardData, eventId);
  const routerHelper = useRouterHelper();
  const isOtherCampaignThanRegistryOn = isWonBannerVisible || isBannerVisible;
  const { shouldRenderBanner, setDismissAddressBanner, isOpenShippingDialog, onCloseShippingDialog, onOpenShippingDialog, dialogViewOpen } = useShippingAddressReminder({
    eventId,
    eventHandle,
    isOtherCampaignThanRegistryOn
  });

  useMount(() => {
    const dashboardVersion = 'v2';
    dashboard.enter({ dashboardVersion });
  });

  const isAdminDashboardV3Enabled = dashboardData.isAdminDashboardV3Enabled;

  return (
    <>
      <DashboardRoot isBannerVisible={isBannerVisible} backgroundColor={isAdminDashboardV3Enabled ? 'mono1' : 'transparent'}>
        <ShippingAddressReminderBanner
          isAddressReminderVisible={shouldRenderBanner && !isWonBannerVisible && !isBannerVisible}
          handleDismissAddressReminderClick={() => {
            setDismissAddressBanner(true);
          }}
          handleAddShippingAddress={onOpenShippingDialog}
        />
        <BookingBanner
          handleDismissBannerClick={(e: React.MouseEvent) => handleDismissBannerClick(e)}
          isBannerVisible={isBannerVisible}
          handleBookingBannerOpen={handleBookingDialogOpenFromBanner}
        />
        <WonBookingBanner isWonBannerVisible={isWonBannerVisible} handleDismissWonBannerClick={(e: React.MouseEvent) => handleDismissWonBannerClick(e)} />

        {/**
         * Adding DashboardContentV3 inside DashboardContentV2 to avoid breaking changes.
         * This sould be called from src/apps/admin/routes/Dashboard/Dashboard.tsx
         * Also, `props.dashboardData` should be merged with `DashboardContentV3`'s data for a single loading experience.
         */}
        {isAdminDashboardV3Enabled ? (
          <DashboardContentV3
            eventHandle={eventHandle}
            eventId={eventId}
            onShareDialogOpen={handleShareDialogOpen}
            onProductSelectionOpen={onProductSelectionOpen}
            fixedHeaderOnMobile={!shouldRenderBanner && !isWonBannerVisible && !isBannerVisible}
          />
        ) : (
          <ContentWrapper>
            <NavigationRoot>
              <PrimaryDashboardContent
                eventId={eventByNameData?.id}
                eventDisplayName={eventDisplayName}
                eventSettingsUrl={routerHelper.buildPath(monorepoRoutePaths.settings)}
                productRows={productRows}
                adminMenuProps={{ eventHandle, adminData: data, loading, error }}
                eventTimestamp={eventByNameData?.info?.tentativeDate?.timestamp}
                eventLocation={eventByNameData?.info?.location}
                handleShareDialogOpen={handleShareDialogOpen}
                handleProductSelectionToggleClick={handleProductSelectionToggleClick}
                handleValuePropositionClick={handleValuePropositionClick}
                isBottomDrawerVisible={isChecklistVisible}
              />
            </NavigationRoot>
            <SidebarRoot>
              {!isMobile ? (
                <DashboardPreview
                  handleShareDialogOpen={handleShareDialogOpen}
                  eventHandle={eventHandle}
                  eventId={eventId}
                  shouldRefreshDashboardPreview={shouldRefreshDashboardPreview}
                />
              ) : isChecklistVisible ? (
                <MobileContent eventHandle={eventHandle} eventId={eventId} />
              ) : null}
            </SidebarRoot>
          </ContentWrapper>
        )}
      </DashboardRoot>

      {/* Hide initial render popups experiment */}
      {!shouldHidePopups && (
        <>
          <BookingDialog isOpen={isBookingDialogOpen} onClose={onBookingDialogClose} sourceIsBanner={bookingDialogSourceIsBanner}></BookingDialog>
          <PrintEarlyAccessDialog isOpen={isPrintEarlyAccessDialogOpen} onClose={onPrintEarlyAccessDialogClose} />
        </>
      )}
      <WonBookingDialog isOpen={isWonBookingDialogOpen} onClose={handleOnWonBookingDialogClose} eventId={eventId} />
      <ProductSelectionDialog isOpen={isProductSelectionOpen} onClose={handleProductSelectionClose} refreshDashboardPreview={refreshDashboardPreview} />
      <ShareDialog isOpen={isOpen} onClose={onClose} guestSiteLink={guestSiteLink} eventPassword={eventPassword} />
      <ShippingAddressReminderDialog
        defaultView={dialogViewOpen}
        eventId={eventId}
        eventHandle={eventHandle}
        isOpen={isOpenShippingDialog}
        onClose={onCloseShippingDialog}
        pageName="AdminDashboard"
      />

      {/* Add back the useToggleIntercomLauncher hook to Dashboard.tsx file if ReEngagementPopup compoent is removed.
      Because ReEngagementPopup contains the logic to handle the intercom launcher. */}
      <ReEngagementPopup isMobile={isMobile} eventId={eventId} eventHandle={eventHandle} />
    </>
  );
};
